import React, { type JSX } from 'react';
import { JoinFull, JoinInner, PowerSettingsNew } from '@mui/icons-material';
import Info from '@mui/icons-material/Info';
import {
    Typography,
    Box,
    Paper,
    Stack,
    Switch,
    TextField,
    FormHelperText,
    MenuItem,
    Select,
    FormControl,
    Button,
} from '@mui/material';
import { CustomSwitchBtn, type GroupState } from 'components/CustomSwitchBtn/CustomSwitchBtn';
import { CustomTransComponent } from 'components/CustomTransComponent';
import {
    useCreateLoadGroupsMutation,
    useGetLoadGroupByIdQuery,
    useUpdateLoadGroupsMutation,
    useUpdateLoadGroupsStatusMutation,
} from '@fiji/common/src/features/loadGroups/loadGroupsApi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    setAppHeaderSubTitle,
    setAppHeaderTitle,
    setMessageContent,
    setIsLoadRefresh,
} from '@fiji/common/src/features/common/commonSlice';
import { useTranslation } from 'react-i18next';

import { BackdropLoader } from 'components/BackdropLoader';
import { CustomModal } from 'components/CustomModal';
import { useTypedSelector } from '@fiji/common';
import { clearLoadGroupsPayload, setFailedLoadTrigger } from '@fiji/common/src/features/loadGroups/loadGroupsSlice';
import { useNewCustomTranslations } from 'hooks';
import { buildLoadGroupMqttTopics } from '@fiji/common/src/utils/loadGroupMqtt';
import { UserProfile } from '@fiji/common/src/types';
import { ApiEndpointType, ApiResponseType } from '@fiji/common/src/features/common/commonTypes';
import { useGetUserProfileQuery } from '@fiji/common/src/features/profile/profileApi';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { subscribeTopic, unsubscribeTopic } from '@fiji/common/src/mqtt_connection/mqttConnection';

const loadGroupsInitialState = {
    description: '',
    isGroupEnabledOrDisabled: true,
    isGroupRedundancyActive: true,
    name: '',
    performGroupSwitchAction: true,
    threshold: 0,
};

const buildLoadGroupTopicIdentityArgs = (loadGroup: any, parentGroupId: any): any => ({
    groupId: loadGroup?.groupId,
    sourceGroupId: loadGroup?.sourceGroupId,
    parentGroupId,
    sourceDeviceId: loadGroup?.sourceDeviceId,
    deviceIds: loadGroup?.deviceIds,
});

const resolveLoadGroupContextArgs = (loadGroup: any, profileData: any, currentOrg: any): any => ({
    realmId: loadGroup?.realmId ?? profileData?.data?.currentRealmId,
    orgId: loadGroup?.orgId ?? currentOrg?.id,
});

const buildLocalLoadGroupTopicsArgs = (loadGroupData: any, profileData: any, currentOrg: any): any => {
    const data = loadGroupData?.data ?? {};
    const loadGroup = data.loadGroup ?? {};
    return {
        ...buildLoadGroupTopicIdentityArgs(loadGroup, data.parentGroupId),
        ...resolveLoadGroupContextArgs(loadGroup, profileData, currentOrg),
    };
};

const getTotalLoads = (loadGroupData: any): number => loadGroupData?.data?.loadGroup?.totalLoads ?? 0;

const shouldSkipLoadGroupQuery = (currentId: any, isDraftMode: boolean): boolean => !currentId || isDraftMode;

const mapLoadIds = (payload: any): any[] => payload?.loadIds?.map?.((load: any) => load?.id) ?? [];

const extractBackendMessage = (error: any): string => {
    const errMsg = error?.data?.errorMessage;
    const firstMsg = Array.isArray(errMsg) ? errMsg[0] : errMsg;
    return firstMsg ?? error?.data?.message ?? 'Failed to create load group';
};

const isValidationError = (error: any): boolean => {
    const status = error?.status;
    return status === 400 || status === 409;
};

const resolveDeviceNameFromData = (data: any): string =>
    data?.loadGroup?.deviceName ?? data?.loadConfig?.[0]?.deviceName ?? '-';

const resolveDeviceName = (loadGroupData: any): string => resolveDeviceNameFromData(loadGroupData?.data);

const resolveGroupName = (loadGroupData: any, draftPayload: any): string => {
    const name = loadGroupData?.data?.loadGroup?.name;
    return name ?? draftPayload?.name ?? '-';
};

const isMqttFailureForTopic = (message: any, messageTopic: string, localTopics: string[]): boolean =>
    message?.type === 'FAILED_LOAD_TRIGGER_FOR_LOAD_GROUP' && localTopics.includes(messageTopic);

const isMqttStateChangeForGroup = (message: any, currentId: any): boolean =>
    message?.type === 'LOAD_GROUP_STATE_CHANGE' && message?.loadGroupId === currentId;

const isMqttStateOrFailure = (message: any): boolean =>
    message?.type === 'LOAD_GROUP_STATE_CHANGE' || message?.type === 'FAILED_LOAD_TRIGGER_FOR_LOAD_GROUP';

const isDraftInputInvalid = (payload: any, loadGroupPayload: any, rejectedName: string): boolean => {
    if (!payload?.loadIds?.length) return true;
    if (!loadGroupPayload.name?.trim()) return true;
    return Boolean(rejectedName) && loadGroupPayload.name === rejectedName;
};

const getGroupStateActionLabel = (groupState: string | undefined): string =>
    groupState === 'ENABLED' ? 'turn on' : 'turn off';

const dispatchStatusMessage = (
    dispatch: any,
    groupName: string,
    stateLabel: string,
    phase: 'initiated' | 'completed'
): void => {
    dispatch(
        setMessageContent({
            isOpen: true,
            message: `${groupName} ${stateLabel} ${phase}`,
        })
    );
};

const handleMqttFailureMessage = (
    message: any,
    messageTopic: string,
    localLoadGroupTopics: string[],
    dispatch: any
): void => {
    if (!isMqttFailureForTopic(message, messageTopic, localLoadGroupTopics)) return;
    dispatch(setFailedLoadTrigger(message));
};

const handleMqttStateChangeMessage = (
    message: any,
    currentId: any,
    lastLoadGroupMqttAtRef: React.MutableRefObject<number>,
    loadGroupData: any,
    dispatch: any
): void => {
    if (!isMqttStateChangeForGroup(message, currentId)) return;
    lastLoadGroupMqttAtRef.current = Date.now();
    const stateLabel = getGroupStateActionLabel(message?.groupState);
    const groupName = loadGroupData?.data?.loadGroup?.name ?? '';
    dispatchStatusMessage(dispatch, groupName, stateLabel, 'completed');
    dispatch(setIsLoadRefresh(true));
};

const updateCurrentStateFromMqtt = (message: any, loadGroupData: any, setCurrentState: React.Dispatch<any>): void => {
    if (!isMqttStateOrFailure(message) || !loadGroupData) return;
    setCurrentState(message?.groupState);
};

const shouldRefetchForPendingState = (
    stateRef: React.MutableRefObject<any>,
    lastLoadGroupMqttAtRef: React.MutableRefObject<number>,
    toggleStartedAt: number
): boolean => stateRef.current === 'PENDING' && lastLoadGroupMqttAtRef.current < toggleStartedAt;

const buildLoadGroupPayload = (loadGroup: any): any => ({
    description: loadGroup.description,
    isGroupEnabledOrDisabled: loadGroup.isGroupEnabledOrDisabled,
    isGroupRedundancyActive: loadGroup.isGroupRedundancyActive,
    name: loadGroup.name,
    performGroupSwitchAction: loadGroup.performGroupSwitchAction,
    threshold: loadGroup.threshold,
});

const handleDraftCreateError = (
    error: any,
    loadGroupPayload: any,
    setNameError: React.Dispatch<React.SetStateAction<string>>,
    setRejectedName: React.Dispatch<React.SetStateAction<string>>,
    dispatch: any
): void => {
    const beMessage = extractBackendMessage(error);
    if (isValidationError(error)) {
        setNameError(beMessage);
        setRejectedName(loadGroupPayload?.name ?? '');
        return;
    }

    dispatch(
        setMessageContent({
            isOpen: true,
            message: beMessage,
            duration: 3000,
        })
    );
};

const resolveRedundancyIcon = (loadGroup: any): JSX.Element | null => {
    const isGroupRedundancyActive = loadGroup?.isGroupRedundancyActive;
    const isGroupRedundant = loadGroup?.isGroupRedundant;

    if (isGroupRedundancyActive) {
        return isGroupRedundant ? <JoinFull sx={{ color: '#39B620' }} /> : <JoinInner sx={{ color: '#CA3C3D' }} />;
    }
    if (isGroupRedundant) return null;
    return <JoinInner sx={{ color: '#727e84' }} />;
};

const navigateBack = (navigate: any, previousRoute: string | undefined): void => {
    if (previousRoute) {
        navigate(previousRoute, {
            state: { subTab: 'load_groups' },
        });
        return;
    }
    navigate(-1);
};

const resolveSaveButtonDisabled = ({
    isDraft,
    isCreateLoadGroupLoading,
    payload,
    loadGroupPayload,
    rejectedName,
    isFormChanged,
    isLoading,
}: {
    isDraft: boolean;
    isCreateLoadGroupLoading: boolean;
    payload: any;
    loadGroupPayload: any;
    rejectedName: string;
    isFormChanged: boolean;
    isLoading: boolean;
}): boolean => {
    if (isDraft) return isCreateLoadGroupLoading || isDraftInputInvalid(payload, loadGroupPayload, rejectedName);
    return !isFormChanged || isLoading || !loadGroupPayload.name?.trim();
};

type LoadGroupStateBoxProps = {
    currentId: any;
    loadGroup: any;
    currentState: any;
    isLoading: boolean;
    isDraft: boolean;
    onToggle: (id: number | string, newState: GroupState) => void;
};

const LoadGroupStateBox = ({
    currentId,
    loadGroup,
    currentState,
    isLoading,
    isDraft,
    onToggle,
}: LoadGroupStateBoxProps): JSX.Element => (
    <Box px={2} py={1} className="flex-center-between" sx={{ backgroundColor: '#f7f8f8', borderRadius: '3px' }}>
        <Box className="align-items-center">
            <PowerSettingsNew sx={{ color: '#727e84' }} />
            <Typography fontWeight={'600'} px={1} fontSize={'14px'} color="#333D43" variant="body2">
                <CustomTransComponent translationKey={'COMMON:STATE'} />
            </Typography>
        </Box>
        <Stack direction={'row'} alignItems={'center'}>
            <CustomSwitchBtn
                id={currentId ?? 1}
                totalLoadCount={loadGroup?.totalLoads}
                enabledLoadCount={loadGroup?.numberOfLoadsEnabled}
                loadGroupName={loadGroup?.name}
                groupState={currentState ?? loadGroup?.groupState}
                onChange={(id, newState): void => onToggle(id, newState)}
                disabled={isLoading || isDraft}
            />
        </Stack>
    </Box>
);

type MultiDeviceInfoBannerProps = {
    loadGroup: any;
};

const MultiDeviceInfoBanner = ({ loadGroup }: MultiDeviceInfoBannerProps): JSX.Element | null => {
    if (!loadGroup?.isMultipleDevice) return null;
    return (
        <Stack
            direction={'row'}
            alignItems={'center'}
            px={3}
            py={1}
            sx={{ backgroundColor: '#007bc10d', borderRadius: '3px' }}
        >
            <Info color="primary" />
            <Typography fontSize={16} fontWeight={400} color="#007bc1" pl={1}>
                <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.PERMISSION_FOR_ALL_DEVICES'} />
            </Typography>
        </Stack>
    );
};

type LoadGroupNameDescriptionInputsProps = {
    loadGroupPayload: any;
    nameError: string;
    setNameError: React.Dispatch<React.SetStateAction<string>>;
    setLoadGroupPayload: React.Dispatch<React.SetStateAction<any>>;
};

const LoadGroupNameDescriptionInputs = ({
    loadGroupPayload,
    nameError,
    setNameError,
    setLoadGroupPayload,
}: LoadGroupNameDescriptionInputsProps): JSX.Element => {
    const payloadObj = loadGroupPayload ?? {};
    return (
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Box flexBasis={'29%'}>
                <TextField
                    fullWidth
                    name="name"
                    label={<CustomTransComponent translationKey={'COMMON:NAME'} />}
                    variant="filled"
                    className="margin-top-18"
                    inputProps={{ maxLength: 32 }}
                    required
                    value={payloadObj.name}
                    onChange={(e): void => {
                        if (nameError) setNameError('');
                        setLoadGroupPayload((prev: any) => ({ ...prev, name: e.target.value }));
                    }}
                    error={Boolean(nameError)}
                    helperText={nameError || undefined}
                />
                <FormHelperText className="text-align-end padding-x-16">{payloadObj.name?.length}/32</FormHelperText>
            </Box>
            <Box flexBasis={'70%'}>
                <TextField
                    id="cloneProfileDescription"
                    fullWidth
                    variant="filled"
                    name="description"
                    label={<CustomTransComponent translationKey={'COMMON:DESCRIPTION'} />}
                    className="margin-top-18"
                    inputProps={{ maxLength: 100 }}
                    value={payloadObj.description}
                    onChange={(e): void =>
                        setLoadGroupPayload((prev: any) => ({ ...prev, description: e.target.value }))
                    }
                />
                <FormHelperText className="text-align-end padding-x-16">
                    {payloadObj.description?.length}/100
                </FormHelperText>
            </Box>
        </Stack>
    );
};

type LoadGroupRedundancyBoxProps = {
    loadGroup: any;
    totalLoads: number;
    threshold: number;
    isDraft: boolean;
    setLoadGroupPayload: React.Dispatch<React.SetStateAction<any>>;
    renderRedundancyIcon: () => any;
};

const LoadGroupRedundancyBox = ({
    loadGroup,
    totalLoads,
    threshold,
    isDraft,
    setLoadGroupPayload,
    renderRedundancyIcon,
}: LoadGroupRedundancyBoxProps): JSX.Element => {
    const redundancyOptions = React.useMemo(() => {
        if (totalLoads < 2) return [];
        return Array.from({ length: totalLoads - 1 }, (_, i) => i + 2);
    }, [totalLoads]);
    const hasEnoughLoads = loadGroup?.totalLoads >= 2;
    const hasMoreThanTwoLoads = loadGroup?.totalLoads > 2;
    const selectDisabled = totalLoads < 2 || isDraft;

    return (
        <Box>
            <Box
                px={2}
                py={1}
                mt={2}
                sx={{
                    backgroundColor: '#f7f8f8',
                    borderRadius: '3px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    {renderRedundancyIcon()}
                    <Typography fontWeight={600} fontSize={14} color="#333D43">
                        <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.REDUNDANCY'} />
                    </Typography>
                    <Switch disabled checked={hasEnoughLoads} />
                    {hasMoreThanTwoLoads && (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography fontWeight={600} fontSize={14} color="#333D43">
                                <CustomTransComponent
                                    translationKey={
                                        'WIDGETS:LOADS_GROUPS.SELECT_ACTIVE_LOADS_BEFORE_ALERT_NOTIFICATION'
                                    }
                                />
                            </Typography>

                            <FormControl variant="standard" sx={{ minWidth: 80, textAlign: 'center' }}>
                                <Select
                                    value={threshold}
                                    onChange={(e): void =>
                                        setLoadGroupPayload((prev: any) => ({
                                            ...prev,
                                            threshold: Number(e.target.value),
                                        }))
                                    }
                                    disabled={selectDisabled}
                                >
                                    {redundancyOptions.map((value) => (
                                        <MenuItem key={value} value={value}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    )}
                </Stack>
            </Box>
        </Box>
    );
};

export const ManageLoadGroups = (): JSX.Element => {
    const { loadGroupId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { isDraft, from: previousRoute, createPayload: draftPayload } = location.state ?? {};
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const [isDraftMode, setIsDraftMode] = React.useState(isDraft);
    const [isDiscardModalOpen, setIsDiscardModalOpen] = React.useState(false);
    const [currentId, setCurrentId] = React.useState(loadGroupId);
    const [initialLoadGroupPayload, setInitialLoadGroupPayload] = React.useState(loadGroupsInitialState);
    const [currentState, setCurrentState] = React.useState<any>(undefined);
    const [loadGroupPayload, setLoadGroupPayload] = React.useState(loadGroupsInitialState);
    // Inline error + rejected-name tracking for the draft create flow, mirroring
    // the single-device create modal: BE validation errors (400/409 — duplicate
    // name) render under the name field and keep the Save button disabled until
    // the user changes the name.
    const [nameError, setNameError] = React.useState<string>('');
    const [rejectedName, setRejectedName] = React.useState<string>('');
    const {
        data: loadGroupData,
        isLoading: isLoadGroupLoading,
        refetch: refetchLoadGroup,
    } = useGetLoadGroupByIdQuery(
        { loadGroupId: currentId },
        { skip: shouldSkipLoadGroupQuery(currentId, isDraftMode) }
    );
    const [createLoadGroup, { isLoading: isCreateLoadGroupLoading }] = useCreateLoadGroupsMutation();
    const totalLoads = getTotalLoads(loadGroupData);
    const loadGroupInfo = loadGroupData?.data?.loadGroup;
    const [updateLoadGroups, { isLoading }] = useUpdateLoadGroupsMutation();
    const [updateLoadGroupsStatus] = useUpdateLoadGroupsStatusMutation();
    useNewCustomTranslations('WIDGETS');
    const { t } = useTranslation();

    const { payload } = useTypedSelector((state) => state.loadGroups);
    const isFormChanged = React.useMemo(
        () => JSON.stringify(loadGroupPayload) !== JSON.stringify(initialLoadGroupPayload),
        [loadGroupPayload, initialLoadGroupPayload]
    );
    const currentOrg = useTypedSelector(selectedOrg);

    const mqttConnectionStatus = useTypedSelector((state) => state['common']['mqttConnectionStatus']);
    const { data: profileData } = useGetUserProfileQuery({}, { refetchOnMountOrArgChange: true }) as ApiEndpointType<
        ApiResponseType<UserProfile>
    >;
    const localLoadGroupTopics = React.useMemo(
        () => buildLoadGroupMqttTopics(buildLocalLoadGroupTopicsArgs(loadGroupData, profileData, currentOrg)),
        [loadGroupData, profileData, currentOrg, loadGroupId]
    );
    const lastLoadGroupMqttAtRef = React.useRef<number>(0);
    const mqttFailedLoadHandler = (message: any, messageTopic: string): void => {
        handleMqttFailureMessage(message, messageTopic, localLoadGroupTopics, dispatch);
        handleMqttStateChangeMessage(message, currentId, lastLoadGroupMqttAtRef, loadGroupData, dispatch);
        updateCurrentStateFromMqtt(message, loadGroupData, setCurrentState);
    };
    React.useEffect(() => {
        if (mqttConnectionStatus === true && localLoadGroupTopics.length) {
            subscribeTopic(localLoadGroupTopics, mqttFailedLoadHandler);
        }

        return (): void => {
            if (localLoadGroupTopics.length) {
                unsubscribeTopic(localLoadGroupTopics);
            }
        };
    }, [mqttConnectionStatus, localLoadGroupTopics, dispatch]);

    const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const stateRef = React.useRef(currentState);

    stateRef.current = currentState;

    React.useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        },
        []
    );

    const handleLoadGroupToggle = async (_id: number | string, newState: GroupState): Promise<void> => {
        if (!currentId) return;

        const isEnabled = newState === 'ENABLED';
        const stateLabel = getGroupStateActionLabel(newState);
        const groupName = loadGroupData?.data?.loadGroup?.name ?? '';
        setCurrentState('PENDING');
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        const toggleStartedAt = Date.now();

        const { error }: any = await updateLoadGroupsStatus({
            loadGroupId: currentId,
            body: {
                performGroupSwitchAction: true,
                isGroupEnabledOrDisabled: isEnabled,
            },
        });
        if (!error) {
            dispatchStatusMessage(dispatch, groupName, stateLabel, 'initiated');

            timerRef.current = setTimeout(() => {
                if (!shouldRefetchForPendingState(stateRef, lastLoadGroupMqttAtRef, toggleStartedAt)) return;
                refetchLoadGroup() as any;
                dispatch(setIsLoadRefresh(true));
                setCurrentState(undefined);
            }, 60000);
        }
    };

    const handleDraftSubmit = async (): Promise<void> => {
        const { error, data }: any = await createLoadGroup({
            ...loadGroupPayload,
            loadIds: mapLoadIds(payload),
            // Suppress global toast — validation errors render inline below,
            // other failures are surfaced via a local toast dispatch.
            suppressErrorToast: true,
        });

        if (error) {
            handleDraftCreateError(error, loadGroupPayload, setNameError, setRejectedName, dispatch);
            return;
        }

        const createdGroup = data?.data?.loadGroup;
        const newId = createdGroup?.id;
        dispatch(clearLoadGroupsPayload());

        dispatch(
            setMessageContent({
                isOpen: true,
                message: t('WIDGETS:LOADS_GROUPS.LOAD_GROUP_CREATED_SUCCESSFULLY'),
                duration: 3000,
            })
        );

        setIsDraftMode(false);
        setCurrentId(newId);
        setNameError('');
        setRejectedName('');

        const payloadOnSubmit = buildLoadGroupPayload(createdGroup);

        setLoadGroupPayload(payloadOnSubmit);
        setInitialLoadGroupPayload(payloadOnSubmit);
        navigate(`/${currentRealmName}/load-groups/${newId}`, {
            state: {
                from: previousRoute || `/${currentRealmName}/load-groups`,
            },
        });
    };

    const handleOnSubmit = async (): Promise<void> => {
        if (isDraftMode) {
            await handleDraftSubmit();
            return;
        }

        if (!currentId) return;

        const { error }: any = await updateLoadGroups({
            loadGroupId: currentId,
            body: loadGroupPayload,
        });

        if (!error) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('WIDGETS:LOADS_GROUPS.CHANGES_HAVE_BEEN_SAVED_SUCCESSFULLY'),
                    duration: 3000,
                })
            );

            setInitialLoadGroupPayload(loadGroupPayload);
        }
    };

    const renderRedundancyIcon = (): any => resolveRedundancyIcon(loadGroupData?.data?.loadGroup);

    const handleCloseButton = (): void => {
        if (isFormChanged) {
            setIsDiscardModalOpen(true);
            return;
        }
        navigateBack(navigate, previousRoute);
    };

    React.useEffect(() => {
        if (isDraftMode && draftPayload) {
            setLoadGroupPayload(draftPayload);
            setInitialLoadGroupPayload(draftPayload);

            return;
        }

        if (loadGroupData?.data) {
            const payloadLoadGroup = buildLoadGroupPayload(loadGroupData.data.loadGroup);

            setLoadGroupPayload(payloadLoadGroup);
            setInitialLoadGroupPayload(payloadLoadGroup);
        }
    }, [loadGroupData?.data, isDraft, isDraftMode, draftPayload]);

    React.useEffect(() => {
        const data = loadGroupData?.data;
        const loadGroup = data?.loadGroup;
        if (data && !loadGroup?.isMultipleDevice) {
            dispatch(setAppHeaderSubTitle(`${resolveDeviceName(loadGroupData)}`));
        }
        dispatch(setAppHeaderTitle(`${resolveGroupName(loadGroupData, draftPayload)}`));

        return () => {
            dispatch(setAppHeaderSubTitle(''));
            dispatch(setAppHeaderTitle(''));
        };
    }, [loadGroupData, draftPayload]);

    React.useEffect(() => {
        if (totalLoads >= 2) {
            setLoadGroupPayload((prev) => ({
                ...prev,
                threshold: prev.threshold >= 2 ? prev.threshold : 2,
            }));
        }
    }, [totalLoads]);

    const isSaveButtonDisabled = (): boolean =>
        resolveSaveButtonDisabled({
            isDraft,
            isCreateLoadGroupLoading,
            payload,
            loadGroupPayload,
            rejectedName,
            isFormChanged,
            isLoading,
        });

    return isLoadGroupLoading ? (
        <BackdropLoader isOpen={true} />
    ) : (
        <>
            <Paper elevation={1} sx={{ borderRadius: 0 }}>
                <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'flex-end'} p={2}>
                    <Button variant={'outlined'} sx={{ mr: 2 }} onClick={() => handleCloseButton()}>
                        <CustomTransComponent translationKey={'COMMON:CLOSE'} />
                    </Button>
                    <Button variant={'contained'} onClick={handleOnSubmit} disabled={isSaveButtonDisabled()}>
                        <CustomTransComponent translationKey={'COMMON:SAVE_LABEL'} />
                    </Button>
                </Stack>
            </Paper>
            <Paper elevation={1} sx={{ p: 3, m: 3 }}>
                <Stack mb={2} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#424e54' }}>
                        <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.LOAD_GROUP_DETAILS'} />
                    </Typography>
                    <LoadGroupStateBox
                        currentId={currentId}
                        loadGroup={loadGroupInfo}
                        currentState={currentState}
                        isLoading={isLoading}
                        isDraft={isDraft}
                        onToggle={handleLoadGroupToggle}
                    />
                </Stack>
                <MultiDeviceInfoBanner loadGroup={loadGroupInfo} />
                <LoadGroupNameDescriptionInputs
                    loadGroupPayload={loadGroupPayload}
                    nameError={nameError}
                    setNameError={setNameError}
                    setLoadGroupPayload={setLoadGroupPayload}
                />
                <LoadGroupRedundancyBox
                    loadGroup={loadGroupInfo}
                    totalLoads={totalLoads}
                    threshold={loadGroupPayload.threshold}
                    isDraft={isDraft}
                    setLoadGroupPayload={setLoadGroupPayload}
                    renderRedundancyIcon={renderRedundancyIcon}
                />
            </Paper>
            <CustomModal
                isOpen={isDiscardModalOpen}
                type="primary"
                header={t('WIDGETS:LOADS_GROUPS.DISCARD_CHANGES_HEADER')}
                actions={[
                    {
                        key: 'go-back-action',
                        label: t('COMMON:GO_BACK'),
                        variant: 'outlined',
                        handleClick: (): void => setIsDiscardModalOpen(false),
                        styles: { borderColor: '#007BC1', color: '#007BC1' },
                    },
                    {
                        key: 'continue-action',
                        label: t('COMMON:CONTINUE'),
                        variant: 'contained',
                        handleClick: (): void => {
                            setIsDiscardModalOpen(false);
                            if (previousRoute) {
                                navigate(previousRoute, {
                                    state: { subTab: 'load_groups' },
                                });
                            } else navigate(-1);
                        },
                        styles: { backgroundColor: '#007BC1', color: '#fff' },
                    },
                ]}
                noDividers
                actionsDivider
            >
                <Stack display="flex" flexDirection="column" paddingBottom={2}>
                    <Typography variant="body1">
                        <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.DISCARD_CHANGES_DESCRIPTION'} />
                    </Typography>
                </Stack>
            </CustomModal>
        </>
    );
};
